import * as React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@krix/ui";
import { useLocale } from "~/lib/locale-provider.tsx";
import { useSession, authClient } from "~/lib/auth-client.ts";
import type { Locale } from "~/lib/i18n.ts";

export interface SiteHeaderProps {
  locale: Locale;
}

export function SiteHeader({ locale }: SiteHeaderProps) {
  const { t, setLocale } = useLocale();
  const { data: session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isAuthed = !!session;
  // Netflix-style: signed-in users see content-focused nav; marketing
  // pages move to footer / user menu. Anonymous users still see the
  // marketing nav.
  const navItems = isAuthed
    ? [
        { to: "/browse", label: t("nav.browse") },
        { to: "/my-list", label: "My list" },
      ]
    : [
        { to: "/browse", label: t("nav.browse") },
        { to: "/pricing", label: "Pricing" },
        { to: "/about", label: "About" },
      ];

  const homeHref = isAuthed ? "/browse" : "/";
  const goSearch = () => navigate("/search");

  return (
    <header
      className={[
        "fixed inset-x-0 top-0 z-40 transition-all duration-200",
        scrolled
          ? "border-b border-[var(--color-border)]/60 bg-[var(--color-background)]/85 backdrop-blur-md"
          : "bg-transparent",
      ].join(" ")}
    >
      <div className="flex h-16 items-center gap-4 px-4 md:gap-6 md:px-8">
        <Link
          to={homeHref}
          aria-label="Krix"
          className="flex items-center gap-2 text-lg font-bold tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
        >
          <span className="bg-brand grid h-8 w-8 place-items-center rounded-lg text-base font-extrabold text-[var(--color-brand-foreground)] shadow-[0_4px_16px_-4px_hsl(265_75%_50%/0.5)]">
            K
          </span>
          Krix
        </Link>

        <nav className="hidden flex-1 items-center gap-1 text-sm md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={[
                "rounded-md px-3 py-1.5 transition-colors",
                location.pathname.startsWith(item.to)
                  ? "bg-[var(--color-secondary)] text-[var(--color-foreground)]"
                  : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
              ].join(" ")}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1.5 md:gap-2">
          <button
            type="button"
            aria-label="Search"
            onClick={goSearch}
            className="grid h-9 w-9 place-items-center rounded-md text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-secondary)] hover:text-[var(--color-foreground)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
          >
            <SearchIcon />
          </button>
          <button
            type="button"
            onClick={() => setLocale(locale === "en" ? "th" : "en")}
            className="grid h-9 min-w-9 place-items-center rounded-md px-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-secondary)] hover:text-[var(--color-foreground)]"
            aria-label={`Switch to ${locale === "en" ? "Thai" : "English"}`}
          >
            {locale === "en" ? "ไทย" : "EN"}
          </button>

          {isAuthed ? (
            <UserMenu
              name={session!.user.name}
              email={session!.user.email}
              image={session!.user.image}
              role={session!.user.role}
              onSignOut={() =>
                authClient.signOut().then(() => {
                  window.location.href = "/";
                })
              }
              signOutLabel={t("nav.logout")}
            />
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/login">{t("nav.signin")}</Link>
              </Button>
              <Button size="sm" asChild className="font-semibold">
                <Link to="/signup">{t("nav.signup")}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

interface RoleStyle {
  label: string;
  bg: string;
  text: string;
  border: string;
}

// Premium role badges visible inside the menu header so staff feel the
// privilege of their account. Customer accounts skip the badge entirely.
const ROLE_STYLES: Record<string, RoleStyle> = {
  superadmin: { label: "Founder", bg: "28 88% 60%", text: "28 88% 96%", border: "28 88% 60%" },
  admin: { label: "Admin", bg: "200 70% 60%", text: "200 70% 96%", border: "200 70% 60%" },
  content_manager: {
    label: "Content",
    bg: "140 50% 50%",
    text: "140 50% 96%",
    border: "140 50% 50%",
  },
  support: { label: "Support", bg: "280 65% 60%", text: "280 65% 96%", border: "280 65% 60%" },
};

function UserMenu({
  name,
  email,
  image,
  role,
  signOutLabel,
  onSignOut,
}: {
  name: string | null;
  email: string;
  image: string | null;
  role: string | undefined;
  signOutLabel: string;
  onSignOut: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const handle = name?.trim() || email.slice(0, email.indexOf("@")) || email;
  const initial = handle.charAt(0).toUpperCase();
  const roleStyle = role ? ROLE_STYLES[role.split(",")[0]?.trim() ?? ""] : undefined;

  // Click-outside + Esc
  React.useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Roving focus with arrow keys within the open menu
  React.useEffect(() => {
    if (!open) return;
    const root = menuRef.current;
    if (!root) return;
    const items = () =>
      Array.from(root.querySelectorAll<HTMLElement>("[role='menuitem']")).filter(
        (el) => !el.hasAttribute("disabled"),
      );
    const onKey = (e: KeyboardEvent) => {
      const all = items();
      const i = all.findIndex((el) => el === document.activeElement);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        all[(i + 1 + all.length) % all.length]?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        all[(i - 1 + all.length) % all.length]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        all[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        all[all.length - 1]?.focus();
      }
    };
    root.addEventListener("keydown", onKey);
    return () => root.removeEventListener("keydown", onKey);
  }, [open]);

  // Close menu when route changes (e.g. user clicks a menu link)
  const lastPath = React.useRef(location.pathname);
  React.useEffect(() => {
    if (lastPath.current !== location.pathname) {
      setOpen(false);
      lastPath.current = location.pathname;
    }
  }, [location.pathname]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu, ${handle}`}
        className={[
          "group flex h-9 items-center gap-2 rounded-full pl-0.5 pr-1 text-sm font-medium",
          "transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2",
          open ? "bg-[var(--color-secondary)]/70" : "hover:bg-[var(--color-secondary)]/50",
        ].join(" ")}
      >
        <Avatar image={image} initial={initial} size={32} />
        <span className="hidden max-w-[140px] truncate pl-0.5 text-[var(--color-foreground)] md:inline">
          {handle}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 top-11 z-50 w-72 origin-top-right overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] text-sm shadow-[var(--shadow-xl)]"
          style={{
            animation: "krix-menu-in 160ms cubic-bezier(0.16, 1, 0.3, 1) both",
            backdropFilter: "blur(12px)",
          }}
        >
          {/* Identity header */}
          <div className="flex items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-secondary)]/30 px-3 py-3">
            <Avatar image={image} initial={initial} size={44} />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-[var(--color-foreground)]">
                {handle}
                {roleStyle && (
                  <span
                    aria-label={`Role: ${roleStyle.label}`}
                    className="shrink-0 rounded-full border px-1.5 py-px text-[9px] font-bold uppercase tracking-wider"
                    style={{
                      backgroundColor: `hsl(${roleStyle.bg} / 0.18)`,
                      color: `hsl(${roleStyle.bg})`,
                      borderColor: `hsl(${roleStyle.border} / 0.4)`,
                    }}
                  >
                    {roleStyle.label}
                  </span>
                )}
              </p>
              <p className="truncate text-xs text-[var(--color-muted-foreground)]">{email}</p>
            </div>
          </div>

          {/* Section 1 — content */}
          <MenuSection>
            <MenuLink to="/browse" active={location.pathname.startsWith("/browse")} icon={<HomeIcon />} onSelect={() => setOpen(false)}>
              Browse
            </MenuLink>
            <MenuLink to="/my-list" active={location.pathname.startsWith("/my-list")} icon={<ListIcon />} onSelect={() => setOpen(false)}>
              My list
            </MenuLink>
            <MenuLink to="/search" active={location.pathname.startsWith("/search")} icon={<SearchIcon />} onSelect={() => setOpen(false)}>
              Search
            </MenuLink>
          </MenuSection>

          <Separator />

          {/* Section 2 — account */}
          <MenuSection>
            <MenuLink to="/settings" active={location.pathname.startsWith("/settings")} icon={<SettingsIcon />} onSelect={() => setOpen(false)}>
              Account & settings
            </MenuLink>
            <MenuLink to="/pricing" active={location.pathname.startsWith("/pricing")} icon={<CreditCardIcon />} onSelect={() => setOpen(false)}>
              Plan & billing
            </MenuLink>
            <MenuLink to="/about" active={location.pathname.startsWith("/about")} icon={<InfoIcon />} onSelect={() => setOpen(false)}>
              About Krix
            </MenuLink>
          </MenuSection>

          <Separator />

          {/* Sign out */}
          <MenuSection>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-destructive)]/12 hover:text-[var(--color-destructive)] focus-visible:bg-[var(--color-destructive)]/12 focus-visible:text-[var(--color-destructive)] focus-visible:outline-none"
            >
              <span className="text-[var(--color-muted-foreground)] group-hover:text-current">
                <SignOutIcon />
              </span>
              {signOutLabel}
            </button>
          </MenuSection>

          {/* keyframes lives in a global animation token; declare inline to
              avoid hunting for the right CSS file when iterating quickly. */}
          <style>{`@keyframes krix-menu-in {
            from { opacity: 0; transform: translateY(-4px) scale(0.98); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }`}</style>
        </div>
      )}
    </div>
  );
}

function Avatar({
  image,
  initial,
  size,
}: {
  image: string | null;
  initial: string;
  size: number;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt=""
        width={size}
        height={size}
        className="rounded-full object-cover ring-2 ring-white/10"
        style={{ width: size, height: size }}
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <span
      aria-hidden
      className="bg-brand grid place-items-center rounded-full font-extrabold text-[var(--color-brand-foreground)] ring-2 ring-white/10"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.42),
        boxShadow: "0 4px 16px -4px hsl(265 75% 50% / 0.4)",
      }}
    >
      {initial}
    </span>
  );
}

function MenuSection({ children }: { children: React.ReactNode }) {
  return <div className="py-1">{children}</div>;
}

function Separator() {
  return <div className="border-t border-[var(--color-border)]" />;
}

function MenuLink({
  to,
  onSelect,
  icon,
  active,
  children,
}: {
  to: string;
  onSelect: () => void;
  icon: React.ReactNode;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onSelect}
      className={[
        "relative flex items-center gap-2.5 rounded-md px-2.5 py-2 mx-1",
        "text-[var(--color-foreground)] transition-colors",
        "hover:bg-[var(--color-secondary)] focus-visible:bg-[var(--color-secondary)] focus-visible:outline-none",
        active ? "bg-[var(--color-secondary)]/60" : "",
      ].join(" ")}
    >
      {active && (
        <span
          aria-hidden
          className="bg-brand absolute left-0 top-1.5 h-[calc(100%-12px)] w-[3px] rounded-r"
        />
      )}
      <span className="text-[var(--color-muted-foreground)]">{icon}</span>
      <span className="flex-1">{children}</span>
    </Link>
  );
}

function HomeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}
function ListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}
function SettingsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function CreditCardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );
}
function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}
function SignOutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform 150ms" }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
